// src/pages/Home.jsx
import React, { useState, useRef, useMemo, memo, useEffect } from "react"; 
import SEO from "../components/SEO";

import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useInView,
} from "framer-motion";
import {
  MapPin,
  Instagram,
  ArrowRight,
  Trophy,
  Star,
  Ticket,
  Utensils,
  Quote,
  ExternalLink,
  Clock,
  Award,
  ChefHat,
  Flame,
  Send,
  Check,
  Sparkles,
  BadgeCheck,
  Users,
  UsersIcon,
} from "lucide-react";

import { ARTICLES, FEATURED_STORY } from "../data/articles/articles";
import { PRESS_LINKS } from "../data/articles/press";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { D10, PIZZAS } from "../data/menu";

/* --- UTILITY FUNCTIONS --- */
const slugify = (name = "") =>
  name
    .toLowerCase()
    .replace(/č|ć/g, "c")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .replace(/đ/g, "d")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

/* --- STATIC DATA --- */
const GALLERY_IMAGES = [
 "/img/reviews/happy-review-1.jpg",
  "/img/reviews/happy-review-2.jpg",
  "/img/reviews/happy-review-3.jpg",
  "/img/reviews/happy-review-4.jpg",
  "/img/reviews/happy-review-5.jpg",
  "/img/reviews/happy-review-6.jpg",
  "/img/reviews/happy-review-7.jpg",
  "/img/reviews/happy-review-8.jpg",
  "/img/reviews/happy-review-9.jpg",
  "/img/reviews/happy-review-10.jpg",
  "/img/reviews/happy-review-11.jpg",
  "/img/reviews/happy-review-12.jpg",
  "/img/reviews/happy-review-13.jpg",
  "/img/reviews/happy-review-14.jpg",
  "/img/reviews/happy-review-15.jpg",
  "/img/reviews/happy-review-16.jpg",
  "/img/reviews/happy-review-17.jpg",
  "/img/reviews/happy-review-18.jpg",
  "/img/reviews/happy-review-19.jpg",
  "/img/reviews/happy-review-20.jpg",
  "/img/reviews/happy-review-21.jpg",
  "/img/reviews/happy-review-22.jpg",
  "/img/reviews/happy-review-23.jpg",
  "/img/reviews/happy-review-24.jpg",
  "/img/reviews/happy-review-25.jpg",
  "/img/reviews/happy-review-26.jpg",
  "/img/reviews/happy-review-27.jpg",
  "/img/reviews/happy-review-28.jpg",
  "/img/reviews/happy-review-29.jpg",
  "/img/reviews/happy-review-30.jpg",
  "/img/reviews/happy-review-31.jpg",
  "/img/reviews/happy-review-32.jpg",
  "/img/reviews/happy-review-33.jpg",
  "/img/reviews/happy-review-34.jpg",
  "/img/reviews/happy-review-35.jpg",
  "/img/reviews/happy-review-36.jpg",];

/**
 * Reviews: Pag + playful + football energy, but not cringe.
 */
const TESTIMONIALS = [
  {
    id: 1,
    text: "Pag has sun, salt, and now this. Pizza that lands like a perfect through ball. No drama, all joy.",
    author: "Mateo R.",
    role: "Local (and proud)",
    rating: "10.0",
    match: "Prosika, 20:15",
  },
  {
    id: 2,
    text: "Came after the beach, stayed until the lights went warm. If you visit Pag and skip D10, that's on you.",
    author: "Sarah J.",
    role: "Guest from London",
    rating: "9.8",
    match: "Sunset session",
  },
  {
    id: 3,
    text: "Service faster than a counter-attack. Pizza bigger than my plans for the evening. Respect.",
    author: "Luka M. (not that one)",
    role: "Bench legend",
    rating: "10.0",
    match: "Late kick-off",
  },
  {
    id: 4,
    text: "It feels like Pag and Napoli shook hands and said: 'OK. Let's make people happy.'",
    author: "Davide B.",
    role: "Food scout",
    rating: "9.5",
    match: "Away day",
  },
  {
    id: 5,
    text: "Simple menu. Strong decisions. No weird experiments. Just a place you want to return to.",
    author: "Ana K.",
    role: "Chef / ultras",
    rating: "9.9",
    match: "Family dinner",
  },
  {
    id: 6,
    text: "If this pizza was a player, it wears #10. Not because it's fancy. Because it's pure fun.",
    author: "Niko S.",
    role: "Academy prospect",
    rating: "10.0",
    match: "Post-match bite",
  },
];

/**
 * Stats: microcopy, not chemistry class.
 */
const STATS = [
  { value: "Prosika", label: "Beach location", icon: MapPin },
  { value: "Sunset", label: "Best kick-off", icon: Sparkles },
  { value: "Type", label: "Ruota di carro", icon: Flame },
  { value: "Fast", label: "Oven → table", icon: Clock },
];

/* --- ANIMATION VARIANTS --- */
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

/* --- HERO CONSTANTS (moved outside component to prevent recreation) --- */
const HERO_COLORS = {
  napoliBlue: "#0087E6",
  babyPink: "#FF99C8",
};

const HERO_STYLES = {
  whiteFill: {
    color: "rgba(255, 255, 255, 0.95)",
    WebkitTextStroke: "2.5px rgba(255, 255, 255, 0.5)",
  },
  ten: {
    textShadow: `0 0 25px ${HERO_COLORS.napoliBlue}99`,
    WebkitTextStroke: `2px ${HERO_COLORS.napoliBlue}`,
  },
  s: {
    textShadow: `0 0 40px ${HERO_COLORS.babyPink}`,
    WebkitTextStroke: `2px ${HERO_COLORS.babyPink}`,
  },
  subtitleGlow: {
    background: `linear-gradient(to right, ${HERO_COLORS.napoliBlue}, ${HERO_COLORS.babyPink})`,
    WebkitBackgroundClip: "text",
    color: "transparent",
    transform: "translateY(2px) scale(1.03)",
  },
  subtextGlow: {
    background: `linear-gradient(90deg, ${HERO_COLORS.napoliBlue}, ${HERO_COLORS.babyPink})`,
  },
  pinkBorder: {
    borderColor: `${HERO_COLORS.babyPink}30`,
  },
};

const Hero = memo(() => {
  return (
    <header className="relative w-full min-h-[100svh] overflow-hidden bg-[#004a80] text-white">
      {/* Background - optimized with will-change for GPU acceleration */}
      <div className="absolute inset-0 z-0">
        <img
          src="/img/1.jpeg"
          alt="D10's Pizza Fantasista on Prosika beach"
          className="w-full h-[110%] object-cover opacity-80 scale-105 will-change-transform"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div 
          className="absolute inset-0 opacity-[0.06]" 
          style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#004a80]/55 via-[#004a80]/30 to-[#2a0040]/95 mix-blend-multiply" />
      </div>

      {/* Decorative Elements - simplified with CSS containment */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1] contain-strict">
        <div className="absolute top-[18%] left-[6%] w-[300px] h-[300px] md:w-[520px] md:h-[520px] rounded-full border border-white/5" />
        <div 
          className="absolute bottom-[10%] right-[10%] w-[220px] h-[220px] md:w-[420px] md:h-[420px] rounded-full border"
          style={HERO_STYLES.pinkBorder} 
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-[100svh] pt-20 md:pt-24">
        <div className="flex-1 flex flex-col justify-center items-center px-6 my-10">
          <div className="text-center w-full max-w-5xl mx-auto">
            
            {/* Tagline */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[1px] w-8 md:w-16 bg-white/30" />
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#0087E6]" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-white/85 uppercase">
                  Prosika Beach • Pag
                </span>
              </div>
              <div className="h-[1px] w-8 md:w-16 bg-white/30" />
            </div>

            {/* Brand stack */}
            <div className="flex flex-col items-center leading-none select-none">
              
              {/* MAIN TITLE: D10S */}
              <h1 className="font-anton text-[26vw] md:text-[20vw] tracking-tighter text-white drop-shadow-2xl flex items-baseline justify-center">
                <span>D</span>
                <div className="relative flex items-baseline mr-[3.2vw] md:mr-[2.2vw]">
                  <span className="relative z-10" style={HERO_STYLES.ten}>
                    10
                  </span>
                  <img
                    src="/img/chilli-apostrophe.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute z-50 w-[7vw] md:w-[5.5vw] h-auto -right-[6vw] md:-right-[4.5vw] top-[0.8vw] md:top-[0.5vw] rotate-[15deg] filter drop-shadow-lg object-contain"
                    loading="eager"
                    decoding="async"
                    width="80"
                    height="80"
                  />
                </div>
                <span className="relative z-10" style={HERO_STYLES.s}>
                  S
                </span>
              </h1>

              {/* SUBTITLE */}
              <h2 className="font-anton text-[8vw] md:text-[5vw] uppercase tracking-wide mt-0 md:mt-1 relative z-20 flex justify-center gap-3 md:gap-5">
                
                {/* THE BACK LAYER: Gradient Glow */}
                <div
                  className="absolute inset-0 -z-10 flex justify-center gap-3 md:gap-5 pointer-events-none select-none blur-[8px] opacity-90"
                  aria-hidden="true"
                  style={HERO_STYLES.subtitleGlow}
                 >
                   <span>PIZZA</span><span>FANTASISTA</span>
                 </div>

                {/* THE FRONT LAYER */}
                <span className="relative z-10" style={HERO_STYLES.whiteFill}>
                  PIZZA
                </span>

                <span className="relative z-10" style={HERO_STYLES.whiteFill}>
                  FANTASISTA
                </span>

              </h2>
            </div>

            {/* Subtext */}
            <div className="mt-12 md:mt-16 relative z-20 max-w-2xl mx-auto">
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] blur-3xl -z-10 pointer-events-none opacity-40"
                style={HERO_STYLES.subtextGlow}
              />
              <p className="font-serif italic text-lg md:text-2xl text-white drop-shadow-md leading-relaxed">
                Beach pizza with <span className="text-[#FFD700] font-medium">matchday energy</span>.
                <br />
                Big slices. Cold drinks. Zero overthinking.
              </p>
              <p className="font-manrope text-white/90 mt-4 text-sm md:text-base leading-relaxed drop-shadow-sm">
                Come sunburnt. Come dressed up. Come after football.
                We'll feed the squad and keep the vibe simple.
              </p>
            </div>

            {/* CTAs */}
            <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 relative z-20">
              <a
                href="#formation"
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#0087E6] text-white rounded-full overflow-hidden transition-all duration-300 hover:bg-[#0070c0] hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(0,135,230,0.3)]"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <Ticket size={18} />
                <span className="relative font-manrope font-bold text-sm tracking-widest uppercase">
                  View the menu
                </span>
                <ArrowRight
                  size={18}
                  className="relative group-hover:translate-x-1 transition-transform"
                />
              </a>

              <a
                href="#manifesto"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 font-manrope font-bold text-sm tracking-widest uppercase active:scale-95 backdrop-blur-sm"
              >
                <Utensils size={18} />
                Why should you visit us
              </a>
            </div>

          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="pb-8 md:pb-12 flex justify-center relative z-20">
          <a href="#formation" className="flex flex-col items-center gap-2 group cursor-pointer">
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#0087E6] uppercase group-hover:text-white transition-colors">
              See the squad
            </span>
            <div className="w-[1px] h-10 bg-gradient-to-b from-[#0087E6] via-[#FF99C8] to-transparent group-hover:h-16 transition-all duration-500 ease-in-out" />
          </a>
        </div>
      </div>
    </header>
  );
});

Hero.displayName = 'Hero';

/* =========================
   STATS BAR (microcopy tweak)
   ========================= */
const StatsBar = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="bg-white py-10 sm:py-14 border-b border-gray-100">
      <div className="container mx-auto px-4">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8"
        >
          {STATS.map((stat, i) => (
            <motion.div key={i} variants={fadeInUp} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#F0F9FF] mb-3">
                <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0087E6]" />
              </div>
              <div className="font-anton text-3xl sm:text-4xl text-[#0087E6] mb-1">
                {stat.value}
              </div>
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-400">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-8">
          <p className="font-manrope text-xs text-gray-500">
            Tip: pizza is a team sport. Order like you mean it.
          </p>
        </div>
      </div>
    </section>
  );
};

/* =========================
   MANIFESTO (WARM OVERLAY VERSION)
   ========================= */
const Manifesto = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="manifesto" className="py-16 sm:py-24 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          {/* IMAGE SIDE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="w-full lg:w-1/2 order-2 lg:order-1"
          >
            {/* Added 'group' for hover effect on image */}
            <div className="relative group">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl relative">
                <img
                  src="img/d10-pizza-beach.jpg"
                  alt="Beachside pizzeria in Pag, Croatia - pizza evening at D10"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />

                {/* --- NEW WARM OVERLAY --- */}
                {/* Layer 1: Dark warm gradient (text readability & mood) */}
                <div className="absolute inset-0 bg-gradient-to-t from-orange-950/80 via-amber-500/30 to-transparent mix-blend-overlay z-10" />
                {/* Layer 2: Subtle uniform tint (removes cold blues) */}
                <div className="absolute inset-0 bg-orange-500/10 mix-blend-multiply z-10" />

                {/* Quote Content */}
                <div className="absolute bottom-6 left-6 right-6 text-white z-20">
                  <Quote className="w-6 h-6 mb-2 opacity-90" />
                  <p className="font-serif italic text-lg sm:text-xl drop-shadow-md">
                    "You come for the pizza. You stay for the evening."
                  </p>
                </div>
              </div>

              {/* The "10" Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="absolute -top-4 -right-4 w-16 h-16 sm:w-20 sm:h-20 bg-[#0087E6] rounded-full flex items-center justify-center text-white font-anton text-2xl sm:text-3xl shadow-lg border-4 border-white z-30"
                aria-label="Number 10 badge"
              >
                10
              </motion.div>
            </div>
          </motion.div>

          {/* TEXT SIDE */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full lg:w-1/2 order-1 lg:order-2"
          >
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1.5 rounded-full mb-4">
              The idea
            </span>

            <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] uppercase leading-[0.9] mb-5">
              A place in Pag you{" "}
              <span className="text-[#0087E6]">want to stay</span>.
            </h2>

            <p className="font-manrope text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
              D10 is a beachside pizzeria in Pag, Croatia, built for evenings
              that don't feel rushed. For long tables, warm air, cold drinks,
              and pizza that's made properly — slow dough, good ingredients, no
              shortcuts. It's the kind of place you plan to stop by… and end up
              staying a while.
            </p>

            <p className="font-manrope text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
              Some people come after the beach. Some after a walk through town.
              Some for dinner, some for the match, some just because it feels
              right. You don't need a reason — just a little time, a little
              appetite, and the mood for a good evening.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-gray-100 bg-[#F8FAFC] p-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                  Beach friendly
                </p>
                <p className="font-manrope text-sm text-gray-600 mt-1">
                  Flip-flops welcome. Salt in the air included.
                </p>
              </div>

              <div className="rounded-xl border border-gray-100 bg-[#F8FAFC] p-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                  Football friendly
                </p>
                <p className="font-manrope text-sm text-gray-600 mt-1">
                  Matches on. Pizza always comes first.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#0087E6] text-white font-manrope font-bold uppercase text-xs tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-md active:scale-95"
              >
                <ArrowRight size={14} />
                View full menu
              </Link>

              <a
                href="#formation"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#F0F9FF] text-[#0087E6] font-manrope font-bold uppercase text-xs tracking-wider rounded-full hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
              >
                <Ticket size={14} />
                Meet the squad
              </a>
            </div>

            <p className="mt-4 text-xs text-gray-400 font-manrope">
              Tip: order one classic and one bold. Sharing is easier than
              choosing.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   TACTICAL BOARD (unchanged UI, same code)
   ========================= */
const pickByRole = (items, predicate, n) => items.filter(predicate).slice(0, n);

const hasTag = (item, tag) =>
  Array.isArray(item.tags) ? item.tags.includes(tag) : false;

const MobilePizzaCard = ({ item }) => {
  const slug = item.slug || slugify(item.name);
  return (
    <Link
      to={`/menu/${slug}`}
      className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-[#0087E6]/30 transition-all active:scale-[0.98]"
      aria-label={`Open ${item.name}`}
    >
      <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-gray-100">
        <img
          src={
            item.detailImage ||
            item.heroImage ||
            item.image ||
            item.img ||
            GALLERY_IMAGES[0]
          }
          alt={item.name}
          className="w-full h-full object-cover scale-90"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <h4 className="font-anton text-base text-[#0F172A] uppercase truncate">
            {item.name}
          </h4>
          <span className="font-manrope font-bold text-[#0087E6] text-sm flex-shrink-0">
            €{item.price}
          </span>
        </div>
        <p className="text-xs text-gray-500 font-manrope line-clamp-1 mt-0.5">
          {item.shortDescription ||
            item.description ||
            "Beach-friendly, match-ready."}
        </p>
      </div>
      <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />
    </Link>
  );
};

const PlayerCard = ({ item, special }) => {
  const slug = item.slug || slugify(item.name);

  return (
    <motion.div whileHover={{ y: -8, transition: { duration: 0.2 } }} className="w-40 lg:w-44">
      <Link to={`/menu/${slug}`} className="block text-center group relative">
        {special && (
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#0087E6] text-white text-[9px] font-bold px-2.5 py-1 rounded uppercase z-20 shadow-md">
            Fan favorite
          </div>
        )}

        <div className="relative mx-auto w-28 h-28 lg:w-32 lg:h-32 rounded-full border-4 border-white shadow-lg overflow-hidden mb-3 transition-colors bg-white group-hover:border-gray-200">
          <img
            src={
              item.detailImage ||
              item.heroImage ||
              item.image ||
              item.img ||
              GALLERY_IMAGES[0]
            }
            alt={item.name}
            className="w-full h-full object-cover scale-90"
            loading="lazy"
          />
        </div>

        <h4 className="font-anton text-lg lg:text-xl text-[#0F172A] uppercase leading-none group-hover:text-[#0087E6] transition-colors">
          {item.name}
        </h4>
        <div className="text-[#0087E6] font-anton text-lg">€{item.price}</div>
        <p className="font-manrope text-[10px] text-gray-400 font-bold uppercase tracking-wider">
          {item.nickname || ""}
        </p>
      </Link>
    </motion.div>
  );
};

const MobileRoleGroup = ({ title, items, isInView, delay = 0, nickname }) => (
  <motion.div
    initial={{ opacity: 0, y: 15 }}
    animate={isInView ? { opacity: 1, y: 0 } : {}}
    transition={{ delay, duration: 0.4 }}
    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
  >
    <div className="bg-[#0087E6] px-4 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-anton text-lg text-white uppercase tracking-wide">
          {title}
        </h3>
        {nickname ? (
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/80">
            {nickname}
          </span>
        ) : null}
      </div>
    </div>
    <div className="p-3 space-y-2">
      {items.map((item) => (
        <MobilePizzaCard key={item.slug || slugify(item.name)} item={item} />
      ))}
    </div>
  </motion.div>
);

const TacticalBoard = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const squad = useMemo(() => {
    const pizzas = PIZZAS.filter((p) => (p.section || "pizze") === "pizze");
    const byId = (id) => pizzas.find((p) => p.id === id);
    const addPosition = (pizza) => (pizza ? { ...pizza, roleLabel: `#${pizza.id}` } : null);

    return {
      strikers: [byId(10), byId(11)].filter(Boolean).map(addPosition),
      attackingMid: [byId(9)].filter(Boolean).map(addPosition),
      wideMids: [byId(7), byId(8)].filter(Boolean).map(addPosition),
      defensiveMid: [byId(6)].filter(Boolean).map(addPosition),
      defenders: [byId(2), byId(3), byId(4), byId(5)].filter(Boolean).map(addPosition),
      goalkeeper: [byId(1)].filter(Boolean).map(addPosition),
    };
  }, []);

  const fanFavoriteSlug =
    squad.attackingMid?.[0]?.slug || squad.strikers?.[0]?.slug;

  return (
    <section id="formation" className="py-16 sm:py-24 overflow-hidden relative" ref={ref}>
      <div
        className="absolute inset-0 bg-[#F0F9FF]"
        style={{
          backgroundImage: `
            linear-gradient(90deg, rgba(34,139,34,0.03) 1px, transparent 1px),
            linear-gradient(rgba(34,139,34,0.02) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-white px-3 py-1.5 rounded-full shadow-sm mb-4">
            Menu preview
          </span>
          <h2 className="text-[#0F172A] font-anton text-4xl sm:text-5xl lg:text-7xl uppercase">
            The Squad
          </h2>
          <p className="font-serif italic text-gray-500 text-sm sm:text-base mt-2">
            Tap any player for the full profile. No transfer fees.
          </p>
        </motion.div>

        {/* Desktop formation */}
        <div className="hidden lg:block relative max-w-5xl mx-auto">
          <div className="absolute inset-0 border-2 border-black/20 rounded-[2.5rem] pointer-events-none bg-gradient-to-b from-emerald-50/60 to-emerald-50/20">
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-black/15" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 border-2 border-black/15 rounded-full" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-black/15 rounded-full" />

            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-24 border-2 border-t-0 border-black/15 rounded-b-lg" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-36 h-12 border-2 border-t-0 border-black/15 rounded-b-md" />

            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-72 h-24 border-2 border-b-0 border-black/15 rounded-t-lg" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-36 h-12 border-2 border-b-0 border-black/15 rounded-t-md" />
          </div>

          <div className="flex flex-col gap-8 py-10 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="flex justify-center gap-24"
            >
              {squad.strikers.map((p) => (
                <PlayerCard key={p.slug || p.name} item={p} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15 }}
              className="flex justify-center"
            >
              {squad.attackingMid.map((p) => (
                <PlayerCard
                  key={p.slug || p.name}
                  item={p}
                  special={(p.slug || slugify(p.name)) === fanFavoriteSlug}
                />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="flex justify-center gap-72"
            >
              {squad.wideMids.map((p) => (
                <PlayerCard key={p.slug || p.name} item={p} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.25 }}
              className="flex justify-center"
            >
              {squad.defensiveMid.map((p) => (
                <PlayerCard key={p.slug || p.name} item={p} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="flex justify-center gap-12"
            >
              {squad.defenders.map((p) => (
                <PlayerCard key={p.slug || p.name} item={p} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35 }}
              className="flex justify-center pt-4"
            >
              {squad.goalkeeper.map((p) => (
                <PlayerCard key={p.slug || p.name} item={p} />
              ))}
            </motion.div>
          </div>
        </div>

        {/* Mobile */}
        <div className="lg:hidden space-y-4">
          <MobileRoleGroup title="Strikers" nickname="The finishers" items={squad.strikers} isInView={isInView} delay={0.1} />
          <MobileRoleGroup
            title="Midfield"
            nickname="Engine room"
            items={[...squad.attackingMid, ...squad.wideMids, ...squad.defensiveMid]}
            isInView={isInView}
            delay={0.2}
          />
          <MobileRoleGroup title="Defense" nickname="Back four" items={squad.defenders} isInView={isInView} delay={0.3} />
          <MobileRoleGroup title="Goalkeeper" nickname="Clean sheet" items={squad.goalkeeper} isInView={isInView} delay={0.4} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-10"
        >
          <Link
            to="/menu"
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0087E6] text-white font-bold uppercase text-sm tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-lg active:scale-95"
          >
            <ArrowRight size={18} />
            View full menu
          </Link>
          <p className="mt-3 text-xs text-gray-400 font-manrope">
            Pro move: order one classic + one spicy. Balance the lineup.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* =========================
   STADIUM / EXPERIENCE (COPY UPGRADE)
   ========================= */
const Stadium = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="stadium" className="py-16 sm:py-24 bg-white overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div className="flex items-center gap-2 text-[#0087E6] mb-4">
              <Star size={18} fill="#0087E6" />
              <span className="font-manrope font-bold text-xs uppercase tracking-widest">
                The vibe
              </span>
            </div>

            <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-5 text-[#0F172A]">
              BEACHFRONT <br />
              <span className="text-[#0087E6]">PIZZA</span> <br />
              DONE RIGHT.
            </h2>

            <p className="font-serif italic text-lg sm:text-xl text-gray-500 mb-4">
              "One slice becomes two. Two becomes 'let's stay a bit more'."
            </p>

            <p className="font-manrope text-gray-600 leading-relaxed mb-4 text-base">
              D10 fits real Pag life. After the sea. Before a night out.
              Between friends. With kids. With visitors. With that one person who always says
              "I'm not hungry" and then eats half your pizza.
            </p>

            <p className="font-manrope text-gray-600 leading-relaxed mb-6 text-base">
              We're by Prosika beach, so you don't need a plan.
              Just follow the smell and the noise.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://maps.app.goo.gl/k6Pda2GE9baab3mx5/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#F0F9FF] text-[#0087E6] font-bold uppercase text-xs tracking-wider rounded-full hover:bg-[#0087E6] hover:text-white transition-colors active:scale-95"
              >
                <MapPin size={14} />
                Get directions
              </a>

              <a
                href={D10.instagram}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#0087E6] text-white font-bold uppercase text-xs tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-md active:scale-95"
              >
                <Instagram size={14} />
                See today's vibe
              </a>
            </div>

            <p className="mt-4 text-xs text-gray-400 font-manrope">
              Takeaway? Yes. Eating it in the car like it's a secret mission? Also yes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="aspect-[4/3] lg:aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/img/vibe.jpeg"
                className="w-full h-full object-cover"
                alt="Pizza vibe in Pag"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   TESTIMONIALS (headline tweak)
   ========================= */
const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <section ref={ref} className="py-24 bg-[#F8FAFC] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0087E6] rounded-full blur-[150px] opacity-5 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#002B5C] rounded-full blur-[150px] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-1.5 rounded-full mb-6 shadow-sm">
              <Trophy size={14} className="text-[#0087E6]" />
              <span className="font-manrope font-bold text-[#002B5C] uppercase text-[10px] tracking-widest">
                Crowd approved
              </span>
            </div>

            <h2 className="font-anton text-5xl md:text-7xl text-[#002B5C] uppercase leading-[0.9]">
              Real people. <span className="text-[#0087E6]">Real talk.</span>
            </h2>

            <p className="font-manrope text-gray-500 mt-6 text-lg">
              No scripts. No "influencer voice". Just island honesty.
            </p>
          </motion.div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {TESTIMONIALS.map((review) => (
            <motion.div
              key={review.id}
              variants={cardVariants}
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5 relative group"
            >
              <div className="absolute top-6 right-6 bg-[#002B5C] text-white font-anton text-lg px-3 py-1 rounded-lg rotate-3 group-hover:rotate-0 transition-transform">
                {review.rating}
              </div>

              <div className="mb-6">
                <Quote className="fill-[#0087E6]/10 text-[#0087E6]" size={40} />
              </div>

              <p className="font-manrope text-lg text-gray-600 font-medium leading-relaxed mb-6">
                "{review.text}"
              </p>

              <div className="w-full h-px bg-gray-100 mb-6" />

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F0F9FF] border border-blue-100 flex items-center justify-center font-anton text-[#0087E6]">
                  {review.author[0]}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <h4 className="font-bold text-[#002B5C] text-sm">
                      {review.author}
                    </h4>
                    <BadgeCheck size={14} className="text-[#0087E6] fill-blue-50" />
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    {review.role}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-dashed border-gray-100 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Moment: <span className="text-[#0087E6]">{review.match}</span>
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* =========================
   PRESS ROOM (tiny copy tweak)
   ========================= */
const PressRoom = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  if (!PRESS_LINKS?.length) return null;

  return (
    <section id="press" className="py-16 sm:py-24 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4"
        >
          <div>
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1.5 rounded-full mb-3">
              Press
            </span>
            <h2 className="text-[#0F172A] font-anton text-3xl sm:text-4xl lg:text-6xl uppercase">
              Press Room
            </h2>
          </div>
          <p className="font-serif italic text-gray-500 sm:max-w-xs sm:text-right text-sm">
            Sometimes people come to Pag to write. Sometimes they also come hungry.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 gap-4"
        >
          {PRESS_LINKS.map((link, i) => (
            <motion.a
              key={`${link.url}-${i}`}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              variants={fadeInUp}
              className="group bg-[#F0F9FF] rounded-2xl hover:shadow-lg hover:bg-white transition-all duration-300 border border-blue-100 overflow-hidden"
              aria-label={`Open press article: ${link.title}`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr]">
                <div className="relative">
                  <div className="aspect-[16/10] sm:aspect-auto sm:h-full w-full overflow-hidden">
                    <img
                      src={link.image}
                      alt={link.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/35 via-transparent to-transparent" />
                  </div>

                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/90 backdrop-blur px-2.5 py-1.5 rounded-full border border-white/60 shadow-sm">
                    {link.logo ? (
                      <img
                        src={link.logo}
                        alt={`${link.source} logo`}
                        className="h-4 w-auto"
                        loading="lazy"
                      />
                    ) : (
                      <span className="font-anton text-xs uppercase text-[#0087E6]">
                        {link.source}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-5 sm:p-6 flex flex-col justify-between min-h-[170px]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {link.date}
                      </span>
                      <h3 className="font-serif text-lg sm:text-xl text-[#0F172A] leading-snug group-hover:text-[#0087E6] transition-colors mt-2">
                        "{link.title}"
                      </h3>
                    </div>

                    <ExternalLink
                      size={16}
                      className="text-[#0087E6]/30 group-hover:text-[#0087E6] transition-colors flex-shrink-0 mt-1"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 mt-5">
                    <span className="text-[#0087E6]">{link.source}</span>
                    <span className="h-px w-3 bg-gray-300" />
                    <span className="group-hover:text-[#0087E6] transition-colors">
                      Read
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center mt-10"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0087E6] text-white font-bold uppercase text-sm tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-lg active:scale-95"
          >
            <ArrowRight size={18} />
            Read the journal
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
/* =========================
   PIZZA "SESSIONS" (with webhook submit)
   ========================= */
const PizzaAcademy = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  // ✅ Put your Make webhook URL here (no env)
  const WEBHOOK_URL =
    "https://hook.eu2.make.com/cm61co498wsly2lo4tfssadgy2cylu4q"; // <-- replace with your real URL

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name?.trim() || !formData.email?.trim()) {
      setError("Please add your name and email.");
      return;
    }

    setSending(true);

    try {
      // Helpful metadata (optional, but useful in Make)
      const payload = {
        source: "d10s-home-private-night",
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: (formData.message || "").trim(),
        page_url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        submitted_at: new Date().toISOString(),
      };

      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // Make.com webhooks accept JSON bodies
        body: JSON.stringify(payload),
      });

      // Some webhooks return 200/204; treat any non-2xx as failure
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }

      setSubmitted(true);

      // optional: clear fields after success
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      setError(
        "Something didn’t send. Please try again, or message us on Instagram."
      );
      console.error("Webhook submit error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="academy"
      className="py-16 sm:py-24 bg-[#F0F9FF] overflow-hidden"
      ref={ref}
    >
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT: OFFER */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-white px-3 py-1.5 rounded-full shadow-sm mb-4">
              Private groups
            </span>

            <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] uppercase leading-[0.95] mb-4">
              Book a <span className="text-[#0087E6]">Pizza Session</span>
            </h2>

            <p className="font-manrope text-gray-600 leading-relaxed mb-6">
              This is for groups who want D10 to feel like “our place for the night”.
              You pick the reason. We take care of the setup, the flow, and the food.
            </p>

            <div className="bg-white/70 border border-blue-100 rounded-2xl p-5 sm:p-6 mb-6">
              <h3 className="font-anton text-lg text-[#0F172A] uppercase mb-2">
                Perfect for
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-manrope text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  birthdays
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  team dinners
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  friends visiting Pag
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  small celebrations
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  “just because” nights
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#0087E6]" />
                  after-wedding dinners
                </li>
              </ul>
            </div>

            <div className="space-y-4 mb-6">
              {[
                {
                  icon: UsersIcon,
                  title: "Your tables reserved",
                  text: "We’ll hold the space for your group so you’re not split around the restaurant.",
                },
                {
                  icon: Utensils,
                  title: "Food plan that fits you",
                  text: "Set menu or share-style “pizza parade”. Easy and fun.",
                },
                {
                  icon: Clock,
                  title: "Timing + flow",
                  text: "Tell us when you want to start. We’ll pace the night properly.",
                },
                {
                  icon: Sparkles,
                  title: "Extras if you want",
                  text: "Cake moment, candles, playlist, little surprises. Optional.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + i * 0.08 }}
                  className="flex gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <item.icon className="w-5 h-5 text-[#0087E6]" />
                  </div>
                  <div>
                    <p className="font-manrope font-bold text-sm text-[#0F172A]">
                      {item.title}
                    </p>
                    <p className="font-manrope text-sm text-gray-600">{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link
              to="/academy"
              className="inline-flex items-center gap-2 text-[#0087E6] font-bold uppercase text-sm tracking-wider hover:text-[#0F172A] transition-colors"
            >
              Request a private night
              <ArrowRight size={16} />
            </Link>

            <p className="mt-4 text-xs text-gray-400 font-manrope">
              Minimum planning energy required. Maximum “that was a good night” outcome.
            </p>
          </motion.div>

          {/* RIGHT: FORM CARD */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-blue-100 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#0087E6]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-[#0087E6]/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#0087E6] flex items-center justify-center">
                    <ChefHat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-anton text-xl text-[#0F172A] uppercase">
                      Get a plan + availability
                    </h3>
                    <p className="font-manrope text-sm text-gray-500">
                      Send the basics. We’ll reply with dates + options.
                    </p>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {!submitted ? (
                    <motion.form
                      key="form"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      {!!error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                          <p className="text-sm font-manrope text-red-700">
                            {error}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          placeholder="Your name"
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-[#F0F9FF]"
                          required
                          disabled={sending}
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          placeholder="you@email.com"
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-[#F0F9FF]"
                          required
                          disabled={sending}
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-2">
                          Details
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) =>
                            setFormData({ ...formData, message: e.target.value })
                          }
                          placeholder={`Example:\n- Date (or range)\n- Group size\n- Occasion\n- Any “must have”`}
                          rows={4}
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0087E6] outline-none font-manrope text-base transition-colors bg-[#F0F9FF] resize-none"
                          disabled={sending}
                        />
                        <p className="mt-2 text-[11px] text-gray-400 font-manrope">
                          Even “8 people, Friday, birthday” is enough.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={sending}
                        className={`w-full py-4 text-white font-anton uppercase text-base tracking-widest rounded-xl transition-colors shadow-md flex items-center justify-center gap-2 active:scale-[0.98] ${
                          sending
                            ? "bg-[#0087E6]/60 cursor-not-allowed"
                            : "bg-[#0087E6] hover:bg-[#0F172A]"
                        }`}
                      >
                        <Send size={18} />
                        {sending ? "Sending..." : "Send request"}
                      </button>

                      <p className="text-center text-xs text-gray-400 font-manrope">
                        We reply with availability + a simple plan.
                      </p>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-10"
                    >
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h4 className="font-anton text-2xl text-[#0F172A] uppercase mb-2">
                        Request sent.
                      </h4>
                      <p className="font-manrope text-gray-600">
                        We’ll get back to you with dates + options.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <p className="mt-6 text-xs text-gray-400 font-manrope">
                  PS: If you bring a football team, we accept tactical debates as background music.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   FAN ZONE (Shuffled 8 Images)
   ========================= */
const FanZone = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  // State to hold the specific 8 images for this session
  const [displayImages, setDisplayImages] = useState([]);

  useEffect(() => {
    // 1. Create a copy of the array
    const allImages = [...GALLERY_IMAGES];
    
    // 2. Shuffle logic (Fisher-Yates or simple sort)
    // Simple random sort is sufficient for a gallery
    const shuffled = allImages.sort(() => 0.5 - Math.random());
    
    // 3. Slice the first 8
    setDisplayImages(shuffled.slice(0, 8));
  }, []);

  return (
    <section id="community" className="py-16 sm:py-24 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10"
        >
          <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-[#F0F9FF] px-3 py-1.5 rounded-full mb-3">
            Community
          </span>
          <h2 className="font-anton text-3xl sm:text-4xl lg:text-6xl text-[#0F172A] uppercase">
            The Fan Zone
          </h2>
          <p className="font-serif italic text-gray-500 mt-2 text-sm">
            Your photos. Your moments. Tag{" "}
            <span className="text-[#0087E6]">@d10spizza</span>
          </p>
        </motion.div>

        {/* Gallery Grid - 4 columns on desktop makes 2 perfect rows of 4 (8 total) */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4"
        >
          {displayImages.map((src, i) => (
            <motion.div
              key={`${src}-${i}`}
              variants={fadeInUp}
              className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-lg transition-shadow"
            >
              <img
                src={src}
                alt="D10 moments"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0087E6]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-4">
                <a
                  href={D10.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white hover:text-[#FFD700] transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  Follow @d10spizza
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center mt-12 pt-8 border-t border-gray-100"
        >
          <a
            href={D10.instagram}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0087E6] text-white font-bold uppercase text-sm tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-lg active:scale-95"
          >
            <Instagram size={18} />
            Follow on Instagram
          </a>
          <p className="mt-3 text-xs text-gray-400 font-manrope">
            We repost. Just don't tag us in topping wars.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
/* =========================
   JOURNAL (unchanged, already good)
   ========================= */
const JournalSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const picks = useMemo(() => {
    if (!ARTICLES?.length) return [];
    const featured = FEATURED_STORY || ARTICLES[0];

    const rest = ARTICLES
      .filter((a) => a?.slug && a.slug !== featured?.slug)
      .sort((a, b) =>
        String(b.datePublished || "").localeCompare(String(a.datePublished || ""))
      );

    return [featured, ...rest.slice(0, 2)].filter(Boolean);
  }, []);

  if (!picks.length) return null;

  return (
    <section id="journal" className="py-16 sm:py-24 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10"
        >
          <div>
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-[#F0F9FF] px-3 py-1.5 rounded-full mb-3">
              Journal
            </span>
            <h2 className="font-anton text-3xl sm:text-4xl lg:text-6xl text-[#0F172A] uppercase">
              D10 stories
            </h2>
            <p className="font-serif italic text-gray-500 mt-2 text-sm sm:text-base max-w-xl">
              Pizza, Pag life, small rituals, big sunsets. Short reads.
            </p>
          </div>

          <Link
            to="/blog"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0087E6] text-white font-bold uppercase text-sm tracking-wider rounded-full hover:bg-[#0F172A] transition-colors shadow-lg active:scale-95 w-full sm:w-auto"
          >
            View all
            <ArrowRight size={18} />
          </Link>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        >
          {picks.map((a, i) => (
            <motion.div key={a.slug} variants={fadeInUp}>
              <Link
                to={`/blog/${a.slug}`}
                className="group block bg-[#F0F9FF] hover:bg-white border border-blue-100 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={a.image || a.heroImage}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />

                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {i === 0 && (
                      <span className="bg-[#0087E6] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded">
                        Featured
                      </span>
                    )}
                    {a.category && (
                      <span className="bg-white/90 backdrop-blur text-[#0F172A] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded">
                        {a.category}
                      </span>
                    )}
                  </div>

                  <div className="absolute bottom-4 left-4 right-4 text-white/85 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                    <span className="text-white">{a.date || ""}</span>
                    <span className="opacity-50">•</span>
                    <span>{a.readTime || ""}</span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-anton text-2xl text-[#0F172A] uppercase leading-[0.95] group-hover:text-[#0087E6] transition-colors">
                    {a.title}
                  </h3>

                  {a.excerpt && (
                    <p className="font-serif italic text-gray-600 text-base mt-3 line-clamp-3">
                      {a.excerpt}
                    </p>
                  )}

                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#0F172A] group-hover:translate-x-1 transition-transform">
                    Read story <ArrowRight size={14} className="text-[#0087E6]" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};



/* =========================
   GLOBAL STYLES
   ========================= */
const GlobalStyles = memo(() => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
    .font-anton { font-family: 'Anton', sans-serif; }
    .font-manrope { font-family: 'Manrope', sans-serif; }
    .font-serif { font-family: 'Playfair Display', serif; }
    html { scroll-behavior: smooth; -webkit-overflow-scrolling: touch; }
    html, body { overflow-x: hidden; position: relative; width: 100%; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #F0F9FF; }
    ::-webkit-scrollbar-thumb { background: #0087E6; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #0F172A; }
    * { -webkit-tap-highlight-color: transparent; -webkit-touch-callout: none; }
    .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
    .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    .contain-strict { contain: strict; }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
    }
    :focus-visible { outline: 2px solid #0087E6; outline-offset: 2px; }
    ::selection { background: #0087E6; color: white; }
  `}</style>
));

GlobalStyles.displayName = 'GlobalStyles';

/* =========================
   MAIN HOME COMPONENT
   ========================= */
export default function Home() {
  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden">
      <SEO
        title="D10 Pizzeria Pag — Pizza by the sea, matchday energy"
        description="D10 Pizza Fantasista on Prosika Beach in Pag. A focused menu, big pizzas, island sunsets, and a little football talk."
        image="/og-preview.png"
        canonical="https://www.d10s.pizza/"
        type="website"
      />

      <GlobalStyles />
      <Navbar />

      <main>
        <Hero />
        <StatsBar />
        <Manifesto />
        <TacticalBoard />
        <Stadium />
        <Testimonials />
        <PressRoom />
        <PizzaAcademy />
        <FanZone />
        <JournalSection />
      </main>

      <Footer />
    </div>
  );
}